import { css } from '@emotion/react';
import {
	ClipboardEvent,
	KeyboardEventHandler,
	ReactElement,
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useRef,
} from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import { useSDK } from '../../../../../app';
import { styled } from '../../../../../styled';
import { Theme } from '../../../../../theme/interface/detailed';
import { debounce } from '../../../../../utils/debounce';
import { isMobile } from '../../../../../utils/isMobile';
import { CustomRenderThumbVertical } from '../../../../customRenderThumbVertical';

type Interface = {
	value: string;
	onChange: (v: string) => void;
	onKeyPress: KeyboardEventHandler<HTMLTextAreaElement>;
	placeholder?: string;
	isPrechat?: boolean;
	textareaRef?: RefObject<HTMLTextAreaElement>;
	setInRow?: (v: boolean) => void;
	inRow?: boolean;
	scrollbar?: RefObject<Scrollbars>;
};

const Container = styled(
	({ className, children }: { className: string; children: ReactNode; isPrechat: boolean }): ReactElement => (
		<div className={className}>{children}</div>
	)
)(
	({
		theme: {
			allSettings: {
				fields: { common },
			},
		},
		inRow,
		max,
	}: {
		max: number;
		theme: Theme;
		inRow?: boolean;
		useAttaches: boolean;
		isPrechat: boolean;
		useEmoji: boolean;
	}) =>
		css`
			flex-grow: 1;

			${isMobile
				? `
			overflow-y: scroll;
			max-height: ${max}px;
			`
				: ''}

			textarea {
				background: ${common.background};
				border: 0;
				box-sizing: border-box;
				color: #222b55;
				font-size: 14px;
				line-height: 20px;
				-ms-overflow-style: none; /* IE and Edge */
				padding: ${inRow ? '4px 8px 4px 4px' : '0 10px 0 4px'};
				resize: none;
				overflow: hidden;
				white-space: break-spaces;
				width: 100%;
				text-overflow: ellipsis;

				&:focus {
					outline: 0;
				}

				&::placeholder {
					color: inherit;
					color: #afb3c0;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}

			.scrollbar {
				box-sizing: border-box;
				& > div {
					box-sizing: border-box;
				}
			}
		`
);

const TEXTAREA_MIN_HEIGHT_CHAT = 30;
const TEXTAREA_MAX_HEIGHT_CHAT = 114;

const TEXTAREA_MIN_HEIGHT_PRECHAT = 100;
const TEXTAREA_MAX_HEIGHT_PRECHAT = 135;

const IN_ROW_TRIGGER_RATE = 0.8;

const TextArea = ({
	onChange,
	onKeyPress,
	scrollbar,
	value,
	isPrechat = false,
	textareaRef,
	inRow,
	setInRow,
	placeholder,
}: Interface): ReactElement => {
	const { welcomeDataStore, replyMessageStore } = useSDK();
	const min = isPrechat ? TEXTAREA_MIN_HEIGHT_PRECHAT : TEXTAREA_MIN_HEIGHT_CHAT;
	const max = isPrechat ? TEXTAREA_MAX_HEIGHT_PRECHAT : TEXTAREA_MAX_HEIGHT_CHAT;
	const currRef = useRef<HTMLTextAreaElement>(null);
	textareaRef = textareaRef || currRef;

	const adjust = useCallback(() => {
		if (textareaRef?.current) {
			textareaRef.current.style.height = '1px';
			const sh = textareaRef.current.scrollHeight;
			textareaRef.current.style.height = (min > sh ? min : sh) + 'px';
		}
	}, [min, textareaRef]);

	const adjustTextField = (): void => {
		const target = textareaRef?.current;
		if (target) {
			adjust();
			if (setInRow) {
				if (target.scrollHeight / max > IN_ROW_TRIGGER_RATE) {
					setInRow(false);
				} else if (target.scrollHeight / max < IN_ROW_TRIGGER_RATE - 0.3) {
					setInRow(true);
				}
			}
		}
	};

	const onChangeHandler = (): void => {
		adjustTextField();
		debounce(() => onChange(textareaRef?.current?.value ?? ''), 10)();
	};

	/**контролируем событие paste в поле ввода */
	const onPasteHandler = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
		const text = e.clipboardData?.getData('text');
		const target = textareaRef?.current;
		e.preventDefault();
		if (text && target) {
			const prevText = target.value;
			onChange(prevText + text);
			target.value = prevText + text;
			adjustTextField();
		}
	};

	useEffect(() => {
		const target = textareaRef?.current;
		if (target) {
			adjust();
			if (setInRow) {
				if (target.scrollHeight / max > IN_ROW_TRIGGER_RATE) {
					setInRow(false);
				} else if (target.scrollHeight / max < IN_ROW_TRIGGER_RATE - 0.3) {
					setInRow(true);
				}
			}
		}
		/**добавление эмоджи */
		const fn = (event: Event): void => {
			const ev = event as CustomEvent;
			if ('detail' in event && target) {
				target.value = ev?.detail;
			}
		};
		target?.addEventListener('addemoji', fn);
		return (): void => {
			target?.removeEventListener('addemoji', fn);
		};
	}, [adjust, max, setInRow, textareaRef]);

	useEffect(() => {
		if (replyMessageStore.repliedMessage && textareaRef?.current) {
			textareaRef.current.focus();
		}
	}, [replyMessageStore.repliedMessage, textareaRef]);

	useEffect(() => {
		if (!value && textareaRef?.current) {
			adjust();
			setInRow && setInRow(true);
		}
	}, [adjust, setInRow, textareaRef, value]);

	useEffect(() => {
		if (!inRow && textareaRef?.current) {
			adjust();
		}
	}, [adjust, inRow, textareaRef]);

	/**очистка textarea */
	useEffect(() => {
		const target = textareaRef?.current;
		if (!value && target) {
			target.value = value;
		}
	}, [textareaRef, value]);

	return (
		<Container
			useAttaches={!!welcomeDataStore.data?.showcase.useAttaches}
			useEmoji={!!welcomeDataStore.data?.showcase.useEmoji}
			inRow={inRow}
			max={max}
			isPrechat={isPrechat}
		>
			<Scrollbars
				renderThumbVertical={CustomRenderThumbVertical}
				className="scrollbar"
				autoHeight
				ref={scrollbar}
				autoHeightMin={min}
				autoHeightMax={max}
			>
				<textarea
					id="ncc-textarea"
					ref={textareaRef}
					onPaste={onPasteHandler}
					defaultValue={value}
					onKeyPress={onKeyPress}
					placeholder={placeholder}
					onChange={onChangeHandler}
				/>
			</Scrollbars>
		</Container>
	);
};

export { TextArea };
